import { AcAppHeader, AcCard, AcCellPopover, AcIcon, AcLoader, AcTooltip, AcTypography } from '@components'
import { ICONS } from '@constants'
import { observer } from 'mobx-react-lite'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useStore } from '@hooks/use-store'
import { toBuurtbase } from '@helpers/redirect-buurtbase-url'
import styles from './ac-week-specifications.module.scss'

type Day = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday'
type Extra = {
  id: number
  name: string
  quantity: number
  completed_at: string | null
  day: Day | string | null
}

const DAY_ORDER: Record<string, number> = {
  monday: 0,
  tuesday: 1,
  wednesday: 2,
  thursday: 3,
  friday: 4,
}

const DAY_SHORT: Record<string, string> = {
  monday: 'Ma',
  tuesday: 'Di',
  wednesday: 'Wo',
  thursday: 'Do',
  friday: 'Vr',
}

type DayCounts = { ma: number; di: number; wo: number; do: number; vr: number }

type Row = {
  id: number
  customer_id: number | null
  customer_name: string
  user_name: string
  lunchers_total: number
  lunchers_per_day: DayCounts
  subtotal: number
  tax: number
  total: number
  extras: Extra[]
}

const DAY_LABELS: Array<[keyof DayCounts, string]> = [
  ['ma', 'Ma'],
  ['di', 'Di'],
  ['wo', 'Wo'],
  ['do', 'Do'],
  ['vr', 'Vr'],
]

const formatEuro = (value: number): string =>
  new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0)

export const AcWeekSpecifications = observer(() => {
  const { id } = useParams()
  const { weeks } = useStore()
  const [openPopover, setOpenPopover] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    weeks.getSpecifications(id).then(res => {
      // eslint-disable-next-line no-console
      console.log('[specifications] response for week', id, res)
    })
    return () => {
      weeks.specifications = null
      weeks.specifications_week = null
    }
  }, [id])

  const rows: Row[] = useMemo(() => {
    const data = weeks.watch_specifications
    if (!Array.isArray(data)) return []
    return data.map((d: any) => {
      const per_day: DayCounts = {
        ma: Number(d?.monday ?? 0),
        di: Number(d?.tuesday ?? 0),
        wo: Number(d?.wednesday ?? 0),
        do: Number(d?.thursday ?? 0),
        vr: Number(d?.friday ?? 0),
      }
      return {
        id: d?.id,
        customer_id: d?.customer?.id ?? null,
        customer_name: d?.customer?.name ?? '-',
        user_name: d?.user?.full_name?.trim() || 'Niet toegewezen',
        lunchers_total: per_day.ma + per_day.di + per_day.wo + per_day.do + per_day.vr,
        lunchers_per_day: per_day,
        subtotal: Number(d?.subtotal ?? 0),
        tax: Number(d?.tax ?? 0),
        total: Number(d?.total ?? 0),
        extras: Array.isArray(d?.extras) ? d.extras : [],
      }
    })
  }, [weeks.watch_specifications])

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => a.customer_name.localeCompare(b.customer_name)),
    [rows]
  )

  const stats = useMemo(() => {
    const totalSpecs = rows.length
    const totalLunchers = rows.reduce((s, r) => s + r.lunchers_total, 0)
    const totalExtras = rows.reduce((s, r) => s + r.extras.length, 0)
    const totalRevenue = rows.reduce((s, r) => s + r.total, 0)
    return { totalSpecs, totalLunchers, totalExtras, totalRevenue }
  }, [rows])

  const data = weeks.watch_specifications
  const week = weeks.watch_specifications_week

  const title = week
    ? `Specifications · week ${week.week} - ${week.year}`
    : `Specifications · week ${id}`

  return (
    <>
      <AcAppHeader title={title} showBackButton />
      {data && rows.length > 0 && (
        <AcCard>
          <div className={styles.stats}>
            <div className={`${styles.stat} ${styles['stat--specs']}`}>
              <span className={styles['stat-label']}># specificaties</span>
              <span className={styles['stat-value']}>{stats.totalSpecs}</span>
            </div>
            <div className={`${styles.stat} ${styles['stat--lunchers']}`}>
              <span className={styles['stat-label']}>Totaal lunchers</span>
              <span className={styles['stat-value']}>{stats.totalLunchers}</span>
            </div>
            <div className={`${styles.stat} ${styles['stat--extras']}`}>
              <span className={styles['stat-label']}>
                Totaal extras
                <AcTooltip label="Elke entry is 1 product op 1 dag voor 1 klant. Bv. 10 bananen op ma + 10 op di = 2.">
                  <span className={styles['info-icon']}>
                    <AcIcon icon={ICONS.INFO} size="sm" />
                  </span>
                </AcTooltip>
              </span>
              <span className={styles['stat-value']}>{stats.totalExtras}</span>
            </div>
            <div className={`${styles.stat} ${styles['stat--revenue']}`}>
              <span className={styles['stat-label']}>Totaal omzet</span>
              <span className={styles['stat-value']}>{formatEuro(stats.totalRevenue)}</span>
            </div>
          </div>
        </AcCard>
      )}
      <AcCard>
        {weeks.is_loading && !data ? (
          <AcLoader />
        ) : sortedRows.length === 0 ? (
          <div className={styles.empty}>
            <AcTypography weight="semibold">Geen specifications voor deze week</AcTypography>
            <AcTypography size="sm">Probeer een andere week of bevestig specifications in Buurtbase.</AcTypography>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Klant</th>
                  <th>Gebruiker</th>
                  <th style={{ textAlign: 'right', width: 110 }}>Lunchers</th>
                  <th style={{ textAlign: 'right', width: 110 }}>Subtotaal</th>
                  <th style={{ width: 110 }}>Extras</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map(r => (
                  <tr key={r.id}>
                    <td>
                      {r.customer_id ? (
                        <a
                          className={styles['customer-link']}
                          href={toBuurtbase(`/customers/${r.customer_id}`)}
                        >
                          <AcTypography size="sm" element="span" weight="semibold">
                            {r.customer_name}
                          </AcTypography>
                        </a>
                      ) : (
                        <AcTypography size="sm" element="span" weight="semibold">
                          {r.customer_name}
                        </AcTypography>
                      )}
                    </td>
                    <td>
                      {r.user_name === 'Niet toegewezen' ? (
                        <span className={styles['user-muted']}>
                          <AcTypography size="sm" element="span" weight="light">
                            {r.user_name}
                          </AcTypography>
                        </span>
                      ) : (
                        <AcTypography size="sm" element="span" weight="light">
                          {r.user_name}
                        </AcTypography>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {r.lunchers_total === 0 ? (
                        <AcTypography size="sm" element="span" weight="light">0</AcTypography>
                      ) : (
                        <AcCellPopover
                          open={openPopover === `lunchers-${r.id}`}
                          onToggle={() => {
                            const key = `lunchers-${r.id}`
                            setOpenPopover(openPopover === key ? null : key)
                          }}
                          label={
                            <AcTypography size="sm" element="span" weight="semibold">
                              {r.lunchers_total}
                            </AcTypography>
                          }
                        >
                          <dl className={styles['day-grid']}>
                            {DAY_LABELS.map(([k, label]) => (
                              <Fragment key={k}>
                                <dt>{label}</dt>
                                <dd>{r.lunchers_per_day[k]}</dd>
                              </Fragment>
                            ))}
                          </dl>
                        </AcCellPopover>
                      )}
                    </td>
                    <td className={styles.num}>
                      <AcTypography size="sm" element="span" weight="light">
                        {formatEuro(r.subtotal)}
                      </AcTypography>
                    </td>
                    <td>
                      {r.extras.length === 0 ? (
                        <AcTypography size="sm" element="span" weight="light">-</AcTypography>
                      ) : (
                        <AcCellPopover
                          open={openPopover === `extras-${r.id}`}
                          onToggle={() => {
                            const key = `extras-${r.id}`
                            setOpenPopover(openPopover === key ? null : key)
                          }}
                          label={
                            <AcTypography size="sm" element="span" weight="light">
                              {r.extras.length} {r.extras.length === 1 ? 'extra' : 'extras'}
                            </AcTypography>
                          }
                        >
                          <ul style={{ margin: 0, paddingLeft: 16 }}>
                            {[...r.extras]
                              .sort((a, b) => {
                                const ao = DAY_ORDER[a.day ?? ''] ?? 99
                                const bo = DAY_ORDER[b.day ?? ''] ?? 99
                                return ao - bo
                              })
                              .map((x, i) => (
                                <li key={i}>
                                  <AcTypography size="sm" element="span" weight="light">
                                    {x.day && DAY_SHORT[x.day] && (
                                      <span style={{ color: '#6b7280', marginRight: 6 }}>
                                        {DAY_SHORT[x.day]}
                                      </span>
                                    )}
                                    {x.name} <span style={{ color: '#888' }}>×{x.quantity}</span>
                                    {x.completed_at && (
                                      <span style={{ color: '#16a34a', marginLeft: 6 }}>✓</span>
                                    )}
                                  </AcTypography>
                                </li>
                              ))}
                          </ul>
                        </AcCellPopover>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AcCard>
    </>
  )
})
